import { useEffect, useState } from "react";
import { Link2, Linkedin, FileDown, Eye, BadgePlus, Mail, AlertTriangle } from "lucide-react";
import { downloadFile } from "@/components/admin/BulkUpload";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PageHeader, Loading, ErrorState } from "@/components/States";
import { BadgeMedal } from "@/components/BadgeMedal";
import { api, formatError } from "@/lib/api";

export const badgeUrl = (id) => `${window.location.origin}/badge/${id}`;

export function ShareButtons({ badge }) {
  const url = badgeUrl(badge.id);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Badge link copied"); } catch (_) { toast.info(url); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" data-testid={`badge-copy-${badge.key}`} onClick={copy} className="rounded-full"><Link2 className="mr-1.5 h-3.5 w-3.5" />Copy link</Button>
      <Button size="sm" asChild className="rounded-full bg-[#0A66C2] hover:bg-[#08539e]">
        <a data-testid={`badge-linkedin-${badge.key}`} href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer"><Linkedin className="mr-1.5 h-3.5 w-3.5" />Share post</a>
      </Button>
    </div>
  );
}

export const awardedDate = (s) => new Date(s.length === 10 ? `${s}T00:00:00` : s);

export const CERT_KINDS = ["mastery", "course"];

export const linkedinAddUrl = (b) => {
  const d = awardedDate(b.awarded_at);
  const q = new URLSearchParams({
    startTask: "CERTIFICATION_NAME", name: b.title, organizationName: "IDDCR Global Institute",
    issueYear: String(d.getFullYear()), issueMonth: String(d.getMonth() + 1), certUrl: badgeUrl(b.id), certId: b.id,
  });
  return `https://www.linkedin.com/profile/add?${q.toString()}`;
};

function ViewStats({ b }) {
  const n = b.views || 0;
  return (
    <p data-testid={`badge-views-${b.key}`} className="flex items-center gap-1.5 text-xs text-ink-3">
      <Eye className="h-3.5 w-3.5 text-aqua" />
      {n === 0 ? "Verification page not opened yet" : <span><b className="text-ink">{n}</b> verification view{n === 1 ? "" : "s"} · last {new Date(b.last_viewed_at).toLocaleDateString()}</span>}
    </p>
  );
}

export function ResendEmailButton({ path, status, testId, onDone }) {
  const [st, setSt] = useState(status);
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(path);
      setSt(data.email_status); onDone?.();
      if (data.email_status === "sent") toast.success(`Certificate email sent (${data.resends_left_today} resend${data.resends_left_today === 1 ? "" : "s"} left today)`);
      else toast.error("Email couldn't be sent right now. Please try again later.");
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  const failed = st === "failed";
  return (
    <div className="flex flex-wrap items-center gap-2">
      {failed && <span data-testid={`${testId}-failed`} className="inline-flex items-center gap-1 text-xs font-medium text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />Email not delivered</span>}
      <Button size="sm" variant={failed ? "default" : "ghost"} disabled={busy} data-testid={testId} onClick={resend}
        className={`rounded-full ${failed ? "bg-amber-700 hover:bg-amber-800" : "text-ink-2"}`}>
        <Mail className="mr-1.5 h-3.5 w-3.5" />{busy ? "Sending..." : "Resend email"}
      </Button>
    </div>
  );
}

function OwnerActions({ b }) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" asChild className="rounded-full bg-[#0A66C2] hover:bg-[#08539e]">
        <a data-testid={`badge-linkedin-add-${b.key}`} href={linkedinAddUrl(b)} target="_blank" rel="noreferrer"><BadgePlus className="mr-1.5 h-3.5 w-3.5" />Add to LinkedIn profile</a>
      </Button>
      <ShareButtons badge={b} />
      {CERT_KINDS.includes(b.kind) && (
        <Button size="sm" variant="outline" data-testid={`badge-certificate-${b.key}`} className="rounded-full border-brand text-ink"
          onClick={() => downloadFile(`/badges/${b.id}/certificate`, `iddcr-learnai-${b.title.toLowerCase().replace(/\s+/g, "-")}-certificate.pdf`)}>
          <FileDown className="mr-1.5 h-3.5 w-3.5" />Download certificate
        </Button>
      )}
      {CERT_KINDS.includes(b.kind) && <ResendEmailButton path={`/badges/${b.id}/resend-email`} status={b.email_status} testId={`badge-resend-${b.key}`} />}
    </div>
  );
}

export default function Badges() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/badges").then((r) => setD(r.data)).catch((e) => setErr(formatError(e))); }, []);
  if (err) return <ErrorState message={err} />;
  if (!d) return <Loading />;
  const icons = Object.fromEntries(d.progress.map((p) => [p.subject_id, p.icon]));
  const req = d.requirements;

  return (
    <div>
      <PageHeader eyebrow="Achievements" title="Your badges" text={`Earn a Mastery badge by scoring ${req.accuracy}%+ across at least ${req.answers} questions from ${req.quizzes}+ quizzes in a subject. Streak badges unlock at ${d.streak_targets.join(" and ")} days.`} />
      {d.earned.length === 0 ? (
        <div data-testid="badges-empty" className="rounded-2xl border border-dashed border-line-strong bg-canvas-2/60 p-8 text-sm text-ink-2">No badges yet — keep practising! Your progress towards each badge is below.</div>
      ) : (
        <div data-testid="badges-earned" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {d.earned.map((b) => (
            <div key={b.id} data-testid={`badge-${b.key}`} className="card-hover flex flex-col items-start gap-4 rounded-2xl border border-line bg-surface p-6">
              <BadgeMedal badge={{ ...b, icon: icons[b.subject_id] }} />
              <div>
                <h3 className="font-bold text-ink">{b.title}</h3>
                <p className="mt-1 text-sm text-ink-2">{b.description}</p>
                <p className="mt-1 text-xs text-ink-3">{b.kind === "course" ? "Completed" : "Awarded"} {awardedDate(b.awarded_at).toLocaleDateString()}</p>
                <div className="mt-2"><ViewStats b={b} /></div>
              </div>
              <OwnerActions b={b} />
            </div>
          ))}
        </div>
      )}
      <h3 className="mb-4 mt-10 text-xl font-bold text-ink">Progress towards Mastery</h3>
      <div data-testid="badges-progress" className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {d.progress.map((p) => {
          const pct = Math.round(Math.min(p.answered / req.answers, p.quizzes / req.quizzes, 1) * 100);
          return (
            <div key={p.subject_id} data-testid={`mastery-progress-${p.subject_id}`} className="rounded-xl border border-line bg-surface p-5">
              <div className="flex items-center justify-between"><p className="font-semibold text-ink">{p.name}</p>{p.mastered && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">Mastered</span>}</div>
              <Progress value={p.mastered ? 100 : pct} className="mt-3 h-1.5" />
              <p className="mt-2 text-xs text-ink-3">{p.answered}/{req.answers} questions · {p.quizzes}/{req.quizzes} subject quizzes · <span className={p.accuracy >= req.accuracy ? "text-emerald-700" : "text-amber-700"}>{p.accuracy}% accuracy</span> (need {req.accuracy}%)</p>
            </div>
          );
        })}
      </div>
      <p className="mt-6 text-sm text-ink-3">Current streak: <b data-testid="badges-streak">{d.streak}</b> day{d.streak === 1 ? "" : "s"}.</p>
    </div>
  );
}
